import type { BusinessWebTemplate, CVTemplate } from '~/types'

/**
 * Opens a new CV in the editor form, optionally on a chosen template, and
 * blank unless it starts from a resume example ("Use this example" on
 * /resume-examples/<slug>).
 *
 * /cv/new needs a session. Visitors without one are sent to registration and
 * the choice is parked in localStorage, so /auth/callback (or the login form)
 * can resume it once the account is live.
 */
const PENDING_NEW_CV_KEY = 'pendingNewCV'

export interface StartCVFormOptions {
  template?: CVTemplate
  webTemplate?: BusinessWebTemplate
  /** Slug of a /resume-examples page whose sample the CV starts from. */
  example?: string
}

export function newCVPath(options: StartCVFormOptions = {}) {
  const query = new URLSearchParams()
  if (options.template) query.set('template', options.template)
  if (options.webTemplate) query.set('webTemplate', options.webTemplate)
  if (options.example) query.set('example', options.example)
  const qs = query.toString()
  return qs ? `/cv/new?${qs}` : '/cv/new'
}

export function useStartCVForm() {
  const user = useSupabaseUser()

  async function startCVForm(options: StartCVFormOptions = {}) {
    if (user.value) {
      return navigateTo(newCVPath(options))
    }

    try {
      localStorage.setItem(PENDING_NEW_CV_KEY, JSON.stringify(options))
    } catch {
      // Private mode: after signing up they land on the CV list instead.
    }
    return navigateTo('/registration')
  }

  /** Returns the /cv/new path for a parked choice and forgets it, or null. */
  function consumePendingNewCVPath(): string | null {
    if (!import.meta.client) return null
    try {
      const raw = localStorage.getItem(PENDING_NEW_CV_KEY)
      if (!raw) return null
      localStorage.removeItem(PENDING_NEW_CV_KEY)
      return newCVPath(JSON.parse(raw) as StartCVFormOptions)
    } catch {
      return null
    }
  }

  return { startCVForm, consumePendingNewCVPath }
}
